import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { parse, plainOf } from '../../lib/docsMarkdown';
import { docsApi } from '../../lib/docs';
import { DocIcon } from './DocIcons';

/**
 * An article, drawn. lib/docsMarkdown.js turns the text into a tree of
 * blocks and runs; this turns the tree into React elements. Never HTML:
 * there is no dangerouslySetInnerHTML anywhere in Documentation, so text
 * that looks like a tag is that text, and a link only ever goes where
 * safeHref() allowed.
 *
 *   refs        what the server resolved for this reader: { docs, tickets,
 *               assets } - a link to an article they may not read is a
 *               locked chip with the author's words and no title of its own
 *   tasks       { checked: Set<number>, toggle(n) } - runbook steps a reader
 *               ticks; numbered in reading order. Omitted, they are drawn
 *               as plain disabled boxes (the editor's preview).
 */

/** A picture from the article's own files: fetched with the reader's credential, shown as a blob. */
function DocImage({ id, alt }) {
  const [url, setUrl] = useState(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let live = true;
    let made = null;
    docsApi.blob(id).then((b) => {
      if (!live) return;
      made = URL.createObjectURL(b);
      setUrl(made);
    }).catch(() => { if (live) setFailed(true); });
    return () => { live = false; if (made) URL.revokeObjectURL(made); };
  }, [id]);
  if (failed) return <span className="dm-img-missing" role="img" aria-label={alt || 'Picture'}>{alt || 'Picture'} (not available)</span>;
  if (!url) return <span className="dm-img-wait" aria-busy="true">{alt || 'Picture'}…</span>;
  return <img className="dm-img" src={url} alt={alt || ''} loading="lazy" />;
}

/** One of the article's files, downloaded with the reader's credential. */
function DocFileLink({ id, name, children }) {
  const [busy, setBusy] = useState(false);
  const get = async () => {
    setBusy(true);
    try {
      const b = await docsApi.blob(id);
      const url = URL.createObjectURL(b);
      const a = document.createElement('a');
      a.href = url;
      a.download = name || 'file';
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch { /* the chip stays; the reader can try again */ }
    setBusy(false);
  };
  return (
    <button type="button" className="dm-chip dm-chip-file" onClick={get} disabled={busy}>
      <DocIcon name="paperclip" />{children}
    </button>
  );
}

function CodeBlock({ lang, text }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(text);
      else {
        const t = document.createElement('textarea');
        t.value = text; document.body.appendChild(t); t.select(); document.execCommand('copy'); t.remove();
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch { /* nothing to do */ }
  };
  return (
    <div className="dm-code">
      <div className="dm-code-head">
        <span className="dm-code-lang">{lang || 'text'}</span>
        <button type="button" className="dm-copy" onClick={copy} aria-label={copied ? 'Copied' : 'Copy the code'}>
          <DocIcon name={copied ? 'check' : 'copy'} />{copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <pre><code>{text}</code></pre>
    </div>
  );
}

const CALLOUT = {
  note: { icon: 'info', label: 'Note' },
  tip: { icon: 'bulb', label: 'Tip' },
  warning: { icon: 'alert', label: 'Warning' },
  danger: { icon: 'stop', label: 'Danger' }
};

function Runs({ runs, refs, k = 'r' }) {
  return (runs || []).map((r, i) => {
    const key = `${k}-${i}`;
    switch (r.t) {
      case 'text': return <React.Fragment key={key}>{r.v}</React.Fragment>;
      case 'br': return <br key={key} />;
      case 'b': return <strong key={key}><Runs runs={r.c} refs={refs} k={key} /></strong>;
      case 'i': return <em key={key}><Runs runs={r.c} refs={refs} k={key} /></em>;
      case 's': return <s key={key}><Runs runs={r.c} refs={refs} k={key} /></s>;
      case 'code': return <code key={key} className="dm-inline-code">{r.v}</code>;
      case 'img':
        return r.kind === 'file'
          ? <DocImage key={key} id={r.id} alt={r.alt} />
          : <img key={key} className="dm-img" src={r.src} alt={r.alt || ''} loading="lazy" referrerPolicy="no-referrer" />;
      case 'ticket': {
        const t = refs?.tickets?.[r.v];
        return t?.href
          ? <Link key={key} className="dm-chip dm-chip-ticket" to={t.href} title={t.title}><DocIcon name="ticket" />{r.v}</Link>
          : <span key={key} className="dm-chip dm-chip-ticket is-plain" title="A ticket"><DocIcon name="ticket" />{r.v}</span>;
      }
      case 'asset': {
        const a = refs?.assets?.[r.v];
        return <Link key={key} className="dm-chip dm-chip-asset" to={a?.href || `/a/${encodeURIComponent(r.v)}`}><DocIcon name="laptop" />{r.v}</Link>;
      }
      case 'link': {
        const label = <Runs runs={r.c} refs={refs} k={key} />;
        if (r.kind === 'doc') {
          const d = refs?.docs?.[r.id];
          if (d?.restricted) {
            return <span key={key} className="dm-chip dm-chip-doc is-locked" title="You do not have access to this article"><DocIcon name="lock" />{label}</span>;
          }
          // On a Help page (refs.helpLinks), another Help article opens in Help.
          const base = refs?.helpLinks && d?.help ? '/help' : '/docs';
          return <Link key={key} className="dm-chip dm-chip-doc" to={`${base}/${r.id}${r.anchor ? `#${r.anchor}` : ''}`} title={d?.title}><DocIcon name="doc" />{label}</Link>;
        }
        if (r.kind === 'file') return <DocFileLink key={key} id={r.id} name={plainOf(r.c)}>{label}</DocFileLink>;
        if (r.kind === 'anchor') {
          return (
            <a key={key} href={r.href} onClick={(e) => {
              const el = document.getElementById(r.href.slice(1));
              if (el) { e.preventDefault(); el.scrollIntoView({ behavior: 'smooth', block: 'start' }); }
            }}>{label}</a>
          );
        }
        if (r.kind === 'app') return <Link key={key} to={r.href}>{label}</Link>;
        return <a key={key} href={r.href} target="_blank" rel="noopener noreferrer" className="dm-ext">{label}</a>;
      }
      default: return null;
    }
  });
}

function Blocks({ blocks, refs, tasks, counter, k = 'b' }) {
  return blocks.map((b, i) => {
    const key = `${k}-${i}`;
    switch (b.t) {
      case 'h': {
        const Tag = `h${Math.min(6, b.level + 1)}`;
        return (
          <Tag key={key} id={b.id} className={`dm-h dm-h${b.level}`}>
            <Runs runs={b.c} refs={refs} k={key} />
            {b.id && <a className="dm-anchor" href={`#${b.id}`} aria-label="Link to this section">#</a>}
          </Tag>
        );
      }
      case 'p': return <p key={key}><Runs runs={b.c} refs={refs} k={key} /></p>;
      case 'hr': return <hr key={key} />;
      case 'code': return <CodeBlock key={key} lang={b.lang} text={b.v} />;
      case 'quote': return <blockquote key={key}><Blocks blocks={b.c} refs={refs} tasks={tasks} counter={counter} k={key} /></blockquote>;
      case 'callout': {
        const c = CALLOUT[b.kind] || CALLOUT.note;
        return (
          <aside key={key} className={`dm-callout dm-callout-${b.kind}`} role="note" aria-label={c.label}>
            <span className="dm-callout-icon" aria-hidden="true"><DocIcon name={c.icon} /></span>
            <div className="dm-callout-body">
              <strong className="dm-callout-label">{c.label}</strong>
              <Blocks blocks={b.c} refs={refs} tasks={tasks} counter={counter} k={key} />
            </div>
          </aside>
        );
      }
      case 'table':
        return (
          <div key={key} className="dm-table-wrap" tabIndex={0} role="region" aria-label="Table">
            <table className="dm-table">
              <thead><tr>{b.head.map((h, j) => <th key={j} style={{ textAlign: b.align[j] }}><Runs runs={h} refs={refs} k={`${key}h${j}`} /></th>)}</tr></thead>
              <tbody>
                {b.rows.map((row, ri) => (
                  <tr key={ri}>{row.map((cell, j) => <td key={j} style={{ textAlign: b.align[j] }}><Runs runs={cell} refs={refs} k={`${key}r${ri}c${j}`} /></td>)}</tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      case 'flow':
        return (
          <ol key={key} className="dm-flow" aria-label="Workflow">
            {b.steps.map((s, j) => (
              <li key={j} className="dm-flow-step">
                <span className="dm-flow-n" aria-hidden="true">{j + 1}</span>
                <div className="dm-flow-card">
                  <div className="dm-flow-title"><Runs runs={s.title} refs={refs} k={`${key}f${j}`} /></div>
                  {(s.who || s.when) && (
                    <div className="dm-flow-meta">
                      {s.who && <span className="dm-flow-who"><DocIcon name="user" />{s.who}</span>}
                      {s.when && <span className="dm-flow-when"><DocIcon name="clock" />{s.when}</span>}
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ol>
        );
      case 'list': {
        const Tag = b.ordered ? 'ol' : 'ul';
        const hasTasks = b.items.some((it) => it.task !== null);
        return (
          <Tag key={key} start={b.ordered && b.start !== 1 ? b.start : undefined} className={hasTasks ? 'dm-tasks' : undefined}>
            {b.items.map((it, j) => {
              const ik = `${key}-${j}`;
              if (it.task === null) {
                return (
                  <li key={ik}>
                    <Runs runs={it.c} refs={refs} k={ik} />
                    {it.blocks.length > 0 && <Blocks blocks={it.blocks} refs={refs} tasks={tasks} counter={counter} k={ik} />}
                  </li>
                );
              }
              counter.n += 1;
              const n = counter.n;
              const done = tasks ? tasks.checked.has(n) : it.task;
              const id = `dm-task-${n}`;
              return (
                <li key={ik} className={`dm-task${done ? ' is-done' : ''}`}>
                  <input id={id} type="checkbox" className="dm-task-box" checked={Boolean(done)} disabled={!tasks}
                         onChange={() => tasks && tasks.toggle(n)} />
                  <div className="dm-task-text">
                    <label htmlFor={id}><Runs runs={it.c} refs={refs} k={ik} /></label>
                    {it.blocks.length > 0 && <Blocks blocks={it.blocks} refs={refs} tasks={tasks} counter={counter} k={ik} />}
                  </div>
                </li>
              );
            })}
          </Tag>
        );
      }
      default: return null;
    }
  });
}

export default function DocMarkdown({ text, blocks: given, refs, tasks, className = '' }) {
  const blocks = useMemo(() => given || parse(text || ''), [given, text]);
  const counter = { n: 0 };
  if (!blocks.length) return <div className={`dm ${className}`}><p className="dm-empty">Nothing written yet.</p></div>;
  return <div className={`dm ${className}`}><Blocks blocks={blocks} refs={refs} tasks={tasks} counter={counter} /></div>;
}
